import {
  ref,
  get,
  child,
  onValue,
  off,
  set,
  update,
  push,
} from 'firebase/database';
import { notifyHandledError } from './ErrorMonitoring';
import Logger from './Logger';
import FirebaseApp from './FirebaseApp';

const { Database } = FirebaseApp;

// Firebase RTDB rejects these characters in a path and `ref()` / `child()`
// throw synchronously when they appear. Mirrors INVALID_PATH_REGEX_ in
// @firebase/database. Paths are frequently built from route params, so
// bot traffic hitting /topics/foo.jpg would otherwise raise a handled
// error on every request (PROD-5655).
// eslint-disable-next-line no-control-regex -- mirrors Firebase's own rule
const INVALID_PATH_CHARS = /[[\].#$\u0000-\u001F\u007F]/;

function isValidDatabasePath(path) {
  return (
    typeof path === 'string' &&
    path.length !== 0 &&
    !INVALID_PATH_CHARS.test(path)
  );
}

// Skipping the call is what keeps bot traffic out of Bugsnag, but a path we
// built ourselves being rejected is a real bug, and silence would hide it.
// Warn rather than notify: logs carry the signal without reopening the error.
// Call sites that expect untrusted input (a route slug, a promo code) check
// the path themselves and never reach here, so this stays quiet in the case
// the guard exists for.
function skipInvalidPath(operation, path) {
  Logger.warn('Skipped Firebase operation on an invalid path', {
    operation,
    path,
  });
  return null;
}

async function getValue(path, iteration = 0) {
  try {
    if (!isValidDatabasePath(path)) return skipInvalidPath('getValue', path);
    const snapshot = await get(child(ref(Database), path));
    const value = snapshot.val();
    return value;
  } catch (error) {
    if (
      typeof error?.message === 'string' &&
      error.message.toLowerCase().includes('client is offline')
    ) {
      notifyHandledError(
        new Error('Firebase fetch failed with client offline error'),
        {
          message: error.message,
          path,
          iteration,
        }
      );
      return null;
    }
    notifyHandledError(error, {
      message: 'Failed to get value from Firebase',
      path,
      iteration,
    });
    return null;
  }
}

async function getSnapshot(path) {
  try {
    if (!isValidDatabasePath(path)) return skipInvalidPath('getSnapshot', path);
    const snapshot = await get(child(ref(Database), path));

    return snapshot;
  } catch (error) {
    notifyHandledError(error, {
      message: 'Error getting snapshot from Firebase',
      path,
    });
    return null;
  }
}
function getRef(path) {
  if (!isValidDatabasePath(path)) return skipInvalidPath('getRef', path);
  return ref(Database, path);
}

async function setValue(path, value) {
  try {
    if (!isValidDatabasePath(path)) return skipInvalidPath('setValue', path);
    const response = await set(ref(Database, path), value);
    return response;
  } catch (error) {
    notifyHandledError(error, {
      message: 'Error setting value in Firebase',
      path,
      value,
    });
    return { error: true };
  }
}

async function updateValue(path, value) {
  try {
    if (!isValidDatabasePath(path)) return skipInvalidPath('updateValue', path);
    const response = await update(ref(Database, path), value);
    return response;
  } catch (error) {
    notifyHandledError(error, {
      message: 'Error updating value in Firebase',
      path,
      value,
    });
    return null;
  }
}

async function generateId(path) {
  try {
    if (!isValidDatabasePath(path)) return skipInvalidPath('generateId', path);
    const res = await push(child(ref(Database), path));
    return res && res.key;
  } catch (error) {
    notifyHandledError(error, {
      message: 'Error generating push id in Firebase',
      path,
    });
    return null;
  }
}

const FirebaseDatabase = {
  isValidDatabasePath,
  getValue,
  getSnapshot,
  getRef,
  onValue,
  off,
  setValue,
  updateValue,
  generateId,
};

export default FirebaseDatabase;
